if (!customElements.get('split-product')) {
  customElements.define('split-product', class extends HTMLElement {
    connectedCallback() {
      this.media = Array.from(this.querySelectorAll('[data-split-media]'));
      this.dots = Array.from(this.querySelectorAll('[data-split-dot]'));
      this.variants = JSON.parse(this.querySelector('[data-split-variants]')?.textContent || '[]');
      this.deliveryRules = JSON.parse(this.querySelector('[data-split-delivery-rules]')?.textContent || '[]');
      this.adaptiveFaqAnswers = new Map(
        Array.from(this.querySelectorAll('[data-split-adaptive-faq-answer]')).map((answer) => [answer, answer.innerHTML])
      );
      this.index = 0;
      this.returnFocus = null;
      this.isolationRecords = [];
      this.querySelector('[data-split-previous]')?.addEventListener('click', () => this.showMedia(this.index - 1));
      this.querySelector('[data-split-next]')?.addEventListener('click', () => this.showMedia(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.showMedia(Number(dot.dataset.splitDot))));
      this.querySelector('[data-split-variant-trigger]')?.addEventListener('click', () => this.openDialog());
      this.querySelectorAll('[data-split-reviews]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => this.scrollToReviews());
      });
      const layer = this.querySelector('[data-split-dialog-layer]');
      if (layer) this.variantSheet = new window.MarmeladeFlyout(layer, {
        panel: layer.querySelector('[data-split-dialog]'),
        handle: layer.querySelector('[data-split-dialog-drag]'),
        dismiss: layer.querySelector('[data-split-dialog-dismiss]'),
        backdrop: layer.querySelector('[data-split-dialog-close]'),
        initialFocus: () => layer.querySelector('[data-split-variant].is-selected'),
      });
      this.querySelectorAll('[data-split-variant]').forEach((button) => button.addEventListener('click', () => this.selectVariant(button.dataset.splitVariant)));
      this.querySelectorAll('.split-product__faq').forEach((detail) => this.bindFaq(detail));
      this.querySelector('[data-split-form]')?.addEventListener('submit', (event) => this.addToCart(event));
      this.setupSwipe();
      this._reviewSectionEvents = new AbortController();
      const initializeReviews = () => {
        this._reviewsCard = document.querySelector('[data-reviews-card]');
        if (!this._reviewsCard) throw new Error('[Marmelade reviews] The boxed reviews section is missing.');
        this.bindJudgeMeWidgets();
        this.openJudgeMeReviewRequest();
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeReviews, { once: true, signal: this._reviewSectionEvents.signal });
      } else initializeReviews();
      const initialVariant = this.variants.find((item) => String(item.id) === String(this.querySelector('[data-split-variant-input]')?.value));
      if (initialVariant) {
        this.updateAddButton(initialVariant);
        this.renderDeliveryTime(initialVariant);
        this.renderAdaptiveFaq(initialVariant);
      }
    }

    showMedia(nextIndex) {
      if (this.media.length < 2) return;
      this.index = (nextIndex + this.media.length) % this.media.length;
      this.media.forEach((item, index) => {
        const active = index === this.index;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (!active) item.querySelector('video')?.pause();
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
      const dots = this.querySelector('[data-split-dots]');
      if (dots) dots.setAttribute('aria-label', `Bild ${this.index + 1} von ${this.media.length}`);
    }

    setupSwipe() {
      const track = this.querySelector('[data-split-media-track]');
      if (!track || this.media.length < 2) return;
      let startX = null;
      track.addEventListener('pointerdown', (event) => { startX = event.clientX; });
      track.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const distance = event.clientX - startX;
        startX = null;
        if (Math.abs(distance) > 45) this.showMedia(this.index + (distance < 0 ? 1 : -1));
      });
      track.addEventListener('pointercancel', () => { startX = null; });
    }

    disconnectedCallback() {
      this.variantSheet?.destroy();
      this._reviewSectionEvents?.abort();
      this._judgeMeEvents?.abort();
      this._reviewRequestObserver?.disconnect();
    }

    bindJudgeMeWidgets() {
      this._judgeMeEvents = new AbortController();
      const widget = this._reviewsCard.querySelector('#judgeme_product_reviews');
      const payload = this._reviewsCard.querySelector('[data-judgeme-review-data]');
      if (payload) {
        window.jdgm ||= {};
        window.jdgm.data ||= {};
        window.jdgm.data.reviewWidget ||= {};
        window.jdgm.data.reviewWidget[this.dataset.productId] = JSON.parse(payload.textContent);
      }
      if (window.jdgmSettings?.review_widget_revamp_enabled) return;
      const legacy = widget.querySelector('.jdgm-legacy-widget-content');
      if (legacy) legacy.style.display = '';
      const initialize = () => {
        if (typeof window.jdgm?.initializeWidgets !== 'function' || typeof window.jdgm.$ !== 'function') {
          throw new Error('[Marmelade reviews] Judge.me widget initialization is unavailable after SDK setup.');
        }
        if (typeof window.jdgm.setupMediaGallery !== 'function') {
          window.jdgm.loadCSS(window.jdgm.widgetPath('media.css'));
          window.jdgm.loadScript('widget/media.js', () => window.jdgm.$(() => {
            if (this._judgeMeEvents.signal.aborted) return;
            if (typeof window.jdgm.setupMediaGallery !== 'function') {
              throw new Error('[Marmelade reviews] Judge.me media module did not initialize.');
            }
            initialize();
          }), true);
          return;
        }
        if (typeof window.jdgm._renderAndSetupReviewForm !== 'function') {
          window.jdgm.loadCSS(window.jdgm.widgetPath('form.css'));
          window.jdgm.loadScript('widget/form.js', () => window.jdgm.$(() => {
            if (this._judgeMeEvents.signal.aborted) return;
            if (typeof window.jdgm._renderAndSetupReviewForm !== 'function') {
              throw new Error('[Marmelade reviews] Judge.me form module did not initialize.');
            }
            initialize();
          }), true);
          return;
        }
        window.jdgm.initializeWidgets(window.jdgm.$(this._reviewsCard));
        const writeReview = widget.querySelector('.jdgm-write-rev-link');
        if (writeReview) {
          const allowed = !window.jdgmSettings?.disable_web_reviews && window.jdgm.checkCustomerAccountRestriction();
          writeReview.style.display = allowed ? '' : 'none';
        }
      };
      document.addEventListener('jdgm.doneSetup', initialize, { signal: this._judgeMeEvents.signal });
      if (window.jdgm?._doneSetup) initialize();
    }

    openJudgeMeReviewRequest() {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('judgeme_review_source') || !params.has('judgeme_token')) {
        return;
      }

      const reviewSource = this._reviewsCard?.querySelector('#judgeme_product_reviews');
      if (!this._reviewsCard || !reviewSource) {
        console.error('[Marmelade reviews] Judge.me review request cannot open because the review widget is missing.');
        return;
      }

      this.scrollToReviews();

      if (window.jdgmSettings?.review_widget_revamp_enabled) return;

      const openForm = () => {
        if (!reviewSource.classList.contains('jdgm--done-setup-widget')) {
          return false;
        }

        const writeReview = reviewSource.querySelector('.jdgm-write-rev-link');
        if (!writeReview) {
          return false;
        }
        if (this.dataset.reviewWriteLabel) {
          writeReview.textContent = this.dataset.reviewWriteLabel;
        }

        if (writeReview.getAttribute('aria-expanded') !== 'true') {
          writeReview.click();
        }

        return true;
      };

      requestAnimationFrame(() => {
        if (openForm()) {
          return;
        }

        this._reviewRequestObserver = new MutationObserver(() => {
          if (!openForm()) {
            return;
          }

          this._reviewRequestObserver.disconnect();
          this._reviewRequestObserver = null;
        });
        this._reviewRequestObserver.observe(reviewSource, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
        });
      });
    }

    scrollToReviews() {
      this._reviewsCard.querySelector('h2').focus({ preventScroll: true });
      this._reviewsCard.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        block: 'start',
      });
    }

    openDialog() {
      this.variantSheet?.open(this.querySelector('[data-split-variant-trigger]'));
    }

    closeDialog(restoreFocus = false) {
      return this.variantSheet?.close({ restoreFocus });
    }

    selectVariant(id) {
      const variant = this.variants.find((item) => String(item.id) === String(id));
      if (!variant) return;
      this.querySelector('[data-split-variant-input]').value = variant.id;
      this.querySelector('[data-split-variant-title]').textContent = variant.title;
      this.querySelector('[data-split-price]').textContent = variant.priceFormatted;
      this.querySelector('[data-split-atc-price]').textContent = variant.priceFormatted;
      this.updateAddButton(variant);
      this.renderDeliveryTime(variant);
      this.renderAdaptiveFaq(variant);
      this.querySelectorAll('[data-split-variant]').forEach((option) => {
        const selected = option.dataset.splitVariant === String(variant.id);
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-pressed', String(selected));
        const check = option.querySelector('[data-split-variant-check]');
        if (check) check.hidden = !selected;
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      this.closeDialog(true);
    }

    updateAddButton(variant) {
      const preorder = window.MarmeladeDelivery.isPreorder(this.getDeliveryRule(variant), variant.deliveryAvailability);
      this.querySelector('[data-split-atc]').disabled = this._addingToCart || !variant.available;
      if (this._addingToCart) return;
      this.querySelector('[data-split-atc-label]').textContent = !variant.available
        ? this.dataset.unavailableLabel
        : preorder ? this.dataset.preorderLabel : this.dataset.addLabel;
    }

    getDeliveryRule(variant) {
      const country = this.querySelector('[data-split-delivery] .split-product__country-flag')?.dataset.countryCode;
      return window.MarmeladeDelivery.findRule(this.deliveryRules, country, variant.deliveryAvailability);
    }

    renderDeliveryTime(variant) {
      const target = this.querySelector('[data-split-delivery-time]');
      if (target) target.textContent = window.MarmeladeDelivery.format(this.getDeliveryRule(variant), this.dataset);
    }

    getFaqShippingDuration(variant) {
      return window.MarmeladeDelivery.format(this.getDeliveryRule(variant), this.dataset, true);
    }

    renderAdaptiveFaq(variant) {
      const rule = this.getDeliveryRule(variant);
      const country = this.querySelector('[data-split-delivery] .split-product__country-flag');
      const replacements = {
        '[product_name]': variant.title || '',
        '[shipping_country]': country?.title || country?.dataset.countryCode || '',
        '[shipping_duration]': this.getFaqShippingDuration(variant),
        '[shipping_price]': rule?.shippingPrice || this.dataset.faqShippingPrice || '',
        '[free_shipping_threshold]': rule?.freeShippingThreshold || this.dataset.faqFreeShippingThreshold || '',
        '[shipping_provider]': rule?.shippingProvider || this.dataset.faqShippingProvider || '',
      };

      this.adaptiveFaqAnswers.forEach((template, answer) => {
        answer.innerHTML = Object.entries(replacements).reduce(
          (html, [placeholder, value]) => html.replaceAll(placeholder, value),
          template
        );
      });
    }

    bindFaq(detail) {
      const summary = detail.querySelector('summary');
      const answer = detail.querySelector('[data-split-faq-answer]');
      if (!summary || !answer) return;

      summary.setAttribute('aria-expanded', String(detail.open));
      answer.style.height = detail.open ? 'auto' : '0px';
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = !detail.open;
        if (shouldOpen) {
          this.querySelectorAll('.split-product__faq[open]').forEach((item) => {
            if (item !== detail) this.setFaqExpanded(item, false);
          });
        }
        this.setFaqExpanded(detail, shouldOpen);
      });
    }

    setFaqExpanded(detail, shouldOpen) {
      const summary = detail.querySelector('summary');
      const answer = detail.querySelector('[data-split-faq-answer]');
      if (!summary || !answer) return;

      answer.getAnimations?.().forEach((animation) => animation.cancel());
      const startHeight = answer.getBoundingClientRect().height;
      if (shouldOpen) detail.open = true;
      const endHeight = shouldOpen ? answer.scrollHeight : 0;
      summary.setAttribute('aria-expanded', String(shouldOpen));

      if (typeof answer.animate !== 'function') {
        detail.open = shouldOpen;
        answer.style.height = shouldOpen ? 'auto' : '0px';
        return;
      }

      const animation = answer.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        {
          duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250,
          easing: 'ease',
        }
      );
      animation.addEventListener('finish', () => {
        detail.open = shouldOpen;
        answer.style.height = shouldOpen ? 'auto' : '0px';
      }, { once: true });
    }

    async addToCart(event) {
      event.preventDefault();
      const variantId = Number(this.querySelector('[data-split-variant-input]')?.value);
      const button = this.querySelector('[data-split-atc]');
      const label = this.querySelector('[data-split-atc-label]');
      const status = this.querySelector('[data-split-status]');
      if (!variantId || this._addingToCart || button.disabled) return;
      this._addingToCart = true;
      button.disabled = true;
      label.textContent = this.dataset.addingLabel;
      status.textContent = '';
      try {
        const response = window.MarmeladeIphoneCompleteCart
          ? null : await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: 'cart-overlay',
            sections_url: window.Shopify.routes.root,
          }),
        });
        const result = response ? await response.json()
          : await window.MarmeladeIphoneCompleteCart.add([{ id: variantId, quantity: 1 }]);
        if (response && !response.ok) throw new Error(result.description || this.dataset.errorLabel);
        try {
          window.MarmeladeAnalytics.capture('product added to cart', {
            surface: 'pdp_addon',
            product_id: this.dataset.productId,
            product_handle: this.dataset.productHandle,
            variant_id: String(variantId),
            item_count: 1,
            quantity: 1
          });
        } catch (error) {
          console.error('[Marmelade analytics] Cart addition was accepted, but tracking failed.', error);
        }
        label.textContent = this.dataset.addedLabel;
        this.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cartAdd: result } }));
      } catch (error) {
        status.textContent = error.message || this.dataset.errorLabel;
      } finally {
        window.setTimeout(() => {
          this._addingToCart = false;
          const selectedId = this.querySelector('[data-split-variant-input]').value;
          const variant = this.variants.find((item) => String(item.id) === String(selectedId));
          if (variant) this.updateAddButton(variant);
        }, 700);
      }
    }
  });
}
